import {Injectable} from '@angular/core';
import {Action, Store} from '@ngrx/store';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {ParticipantsListService} from './participants-list.service';
import * as fromRoot from '../../../../state/app.state';
import {Observable, of} from "rxjs";
import {catchError, map, mergeMap, tap} from "rxjs/operators";
import {
  FetchParticipantInformationModel
} from "../models/fetch-participant-information.model";
import {
  FetchRelationshipQuestionModel
} from "../models/fetch-relationship-question.model";
import {
  FetchHouseholdQuestionModel
} from "../models/fetch-household-question.model";
import {
  FetchPhiAndPiiQuestionModel
} from "../models/fetch-phi-and-pii-question.model";
import {
  FetchParticipantsListModel
} from "../models/fetch-participants-list.model";
import {
  FetchRelationsFilterModel
} from "../models/fetch-relations-filter.model";
import {SaveAnswersModel} from "../models/save-answers.model";
import {CommonService} from "../../../shared/services/common.service";
import {UpdateRelationModel} from "../models/update-relation.model";
import {
  EditProfileFailure,
  EditProfileRequest,
  EditProfileSuccess,
  FetchHouseholdQuestionFailure,
  FetchHouseholdQuestionRequest,
  FetchHouseholdQuestionSuccess,
  FetchParticipantInformationFailure,
  FetchParticipantInformationRequest,
  FetchParticipantInformationSuccess,
  FetchParticipantsListFailure,
  FetchParticipantsListRequest,
  FetchParticipantsListSuccess,
  FetchPhiAndPiiQuestionFailure,
  FetchPhiAndPiiQuestionRequest,
  FetchPhiAndPiiQuestionSuccess,
  FetchRelationsFilterFailure,
  FetchRelationsFilterRequest,
  FetchRelationsFilterSuccess,
  FetchRelationshipQuestionFailure,
  FetchRelationshipQuestionRequest,
  FetchRelationshipQuestionSuccess,
  MarkHipaaConsentFailure,
  MarkHipaaConsentRequest,
  MarkHipaaConsentSuccess,
  SaveAnswersFailure,
  SaveAnswersRequest,
  SaveAnswersSuccess,
  UpdateRelationFailure,
  UpdateRelationRequest,
  UpdateRelationSuccess
} from "./participants-list.actions";
import {SetProfile} from "../../../shared/core/shared.actions";
import {Router} from "@angular/router";
import {MarkHipaaConsentModel} from "../models/mark-hipaa-consent.model";
import {EditProfileModel} from "../../../shared/models/edit-profile.model";
import {profile} from "../../../shared/core/shared.selectors";
import {isLar} from "../../../shared/constants/common.constants";


@Injectable()
export class ParticipantsListEffects {
  fetchParticipantsList$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchParticipantsListRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap((payload) =>
        this.participantListService.fetchParticipantsList(payload).pipe(
          map((response: FetchParticipantsListModel['response']) => {
            const {data, message} = response;
            data?.list?.forEach((item) => {
              let consentType = 0;
              if (item.isLar === -1 && !item.hipaaConsentDate && !item.hasAnsweredLARQuestions) {
                consentType = 0;
              } else if (item.isLar === 0) {
                if (!item.hipaaConsentDate || !item.hasAnsweredLARQuestions) {
                  consentType = 3;
                } else {
                  consentType = 2;
                }
              } else if (item.isLar === 1) {
                if (!item.relation || !item.hipaaConsentDate || !item.hasAnsweredLARQuestions) {
                  consentType = 0;
                } else {
                  consentType = 2;
                }
              } else if (item.isLar === 2) {
                consentType = 3;
              }

              item['consentType'] = consentType;
            })

            return FetchParticipantsListSuccess({participantsList: data, message});
          }),
          catchError(() => {
            return of(FetchParticipantsListFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchParticipantsListSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchParticipantsListFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );
  fetchParticipantInformation$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchParticipantInformationRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap((payload: FetchParticipantInformationModel['request']) =>
        this.participantListService.fetchParticipantInformation(payload).pipe(
          map((response: FetchParticipantInformationModel['response']) => {
            const {data, message} = response;
            return FetchParticipantInformationSuccess({participantInformation: data, message});
          }),
          catchError((error) => {
            return of(FetchParticipantInformationFailure(error.message));
          }),
          tap((action: any) => {
            if (action.type === FetchParticipantInformationSuccess.type) {
              // Code to execute on API Success Action dispatch

            } else if (action.type === FetchParticipantInformationFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );
  fetchRelationshipQuestion$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchRelationshipQuestionRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap((payload: FetchRelationshipQuestionModel['request']) =>
        this.participantListService.fetchObQuestions(payload).pipe(
          map((response: FetchRelationshipQuestionModel['response']) => {
            const {data, message} = response;
            const relationshipQuestion = data[0];
            return FetchRelationshipQuestionSuccess({
              relationshipQuestion,
              message
            });
          }),
          catchError((error) => {
            return of(FetchRelationshipQuestionFailure(error.message));
          }),
          tap((action: any) => {
            if (action.type === FetchRelationshipQuestionSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchRelationshipQuestionFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );
  fetchHouseholdQuestion$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchHouseholdQuestionRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap((payload: FetchHouseholdQuestionModel['request']) =>
        this.participantListService.fetchObQuestions(payload).pipe(
          map((response: FetchHouseholdQuestionModel['response']) => {
            const {data, message} = response;
            const householdQuestion = data[0];
            return FetchHouseholdQuestionSuccess({
              householdQuestion,
              message
            });
          }),
          catchError((error) => {
            return of(FetchHouseholdQuestionFailure(error.message));
          }),
          tap((action: any) => {
            if (action.type === FetchHouseholdQuestionSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchHouseholdQuestionFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );
  fetchPhiAndPiiQuestions$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchPhiAndPiiQuestionRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap((payload: FetchPhiAndPiiQuestionModel['request']) =>
        this.participantListService.fetchObQuestions(payload).pipe(
          map((response: FetchPhiAndPiiQuestionModel['response']) => {
            const {data, message} = response;
            const phiAndPiiQuestionList: any = {list: data};
            return FetchPhiAndPiiQuestionSuccess({
              phiAndPiiQuestionList,
              message
            });
          }),
          catchError((error) => {
            return of(FetchPhiAndPiiQuestionFailure(error.message));
          }),
          tap((action: any) => {
            if (action.type === FetchPhiAndPiiQuestionSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchPhiAndPiiQuestionFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );
  fetchRelationsFilter$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchRelationsFilterRequest),
      map((action: any) => {
        return action;
      }),
      mergeMap(() =>
        this.participantListService.fetchRelationsFilter().pipe(
          map((response: FetchRelationsFilterModel['response']) => {
            let {data, message} = response;
            return FetchRelationsFilterSuccess({relationshipFilter: data, message});
          }),
          catchError((error) => {
            return of(FetchRelationsFilterFailure(error.message));
          }),
          tap((action: any) => {
            if (action.type === FetchRelationsFilterSuccess.type) {
              // Code to execute on API Success Action dispatch

            } else if (action.type === FetchRelationsFilterFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );
  updateRelation$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(UpdateRelationRequest),
      map((action) => {
        return action;
      }),
      mergeMap(({payload, callback}) =>
        this.participantListService.updateRelation(payload).pipe(
          map((response: UpdateRelationModel['response']) => {
            const {message} = response;

            return UpdateRelationSuccess({message});
          }),
          catchError(() => {
            return of(UpdateRelationFailure());
          }),
          tap((action) => {
            this.commonService.closeDialog();
            if (action.type === UpdateRelationSuccess.type) {
              // Code to execute on API Success Action dispatch
              if (callback) {
                callback(true);
              }
            } else if (action.type === UpdateRelationFailure.type) {
              // Code to execute on API Failure Action dispatch
              if (callback) {
                callback(false);
              }
            }
          })
        )
      )
    )
  );
  userId: string = '';
  editProfile$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(EditProfileRequest),
      map((action: any) => {
        this.userId = action.payload.forUserId
        return action.payload;
      }),
      mergeMap((payload: EditProfileModel['request']) =>
        this.participantListService.editProfile(payload).pipe(
          map((response: EditProfileModel['response']) => {
            const {message} = response;
            return EditProfileSuccess({message});
          }),
          catchError(() => {
            return of(EditProfileFailure());
          }),
          tap((action: any) => {
            if (action.type === EditProfileSuccess.type) {
              // Code to execute on API Success Action dispatch
              if (payload['isLar']) {
                this.router.navigate([`adult/my-participants/relation/${this.userId}`], {queryParams: { isLar: payload['isLar'] }});
                return
              } else {
                this.commonService.removeAuthenticationToken();
                this.commonService.remove2FAT();
                this.commonService.removeLoginOTPToken();
                this.commonService.removeRegisterOtpToken();
                this.commonService.removeRegisterToken();

                this.router.navigate((['auth/message']));
                return
              }
            } else if (action.type === EditProfileFailure.type) {
              // Code to execute on API Failure Action dispatch

            }
          })
        )
      )
    )
  );
  participantId: number | null;
  markHipaaConsent$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(MarkHipaaConsentRequest),
      map((action: any) => {
        this.participantId = action.payload
        return action.payload;
      }),
      mergeMap((payload) =>
        this.participantListService.markHipaaConsent(payload).pipe(
          map((response: MarkHipaaConsentModel['response']) => {
            const {message} = response;
            return MarkHipaaConsentSuccess({message});
          }),
          catchError((error) => {
            return of(MarkHipaaConsentFailure(error.message));
          }),
          tap((action: any) => {
            if (action.type === MarkHipaaConsentSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.commonService.closeDialog();
              // this.router.navigate([`adult/my-participants/additional-details/${this.participantId}`]);
            } else if (action.type === MarkHipaaConsentFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );
  fromPhiPiiDetailsScreen: boolean;
  saveAnswers$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(SaveAnswersRequest),
      map((action) => {
        this.fromPhiPiiDetailsScreen = action.fromPhiPiiDetailsScreen;
        return action;
      }),
      mergeMap(({payload, callback}) =>
        this.participantListService.saveAnswers(payload).pipe(
          map((response: SaveAnswersModel['response']) => {
            const {data, message} = response;
            const saveAnswersInfo = {};
            if (data) {
              if (data.token) {
                const token = data.token;
                this.commonService.setAuthenticationToken(token);
              }
              if (data.profile) {
                saveAnswersInfo['profile'] = {
                  ...data.profile,
                  roleName: data.profile.role.value,
                  role: data.profile.role.key,
                }

              }
            }
            return SaveAnswersSuccess({saveAnswersInfo, message});
          }),
          catchError(() => {
            return of(SaveAnswersFailure());
          }),
          tap((action) => {
            this.commonService.closeDialog();
            if (action.type === SaveAnswersSuccess.type) {
              // Code to execute on API Success Action dispatch
              if (action.saveAnswersInfo.data) {
                this.store.dispatch(SetProfile({profile: action.saveAnswersInfo.data.profile}));
                this.commonService.notification(
                  action.message,
                  'success'
                );
              }

              if (this.fromPhiPiiDetailsScreen) {
                this.router.navigate(['adult/my-participants'])
              }
              if (callback) {
                callback(true);
              }
            } else if (action.type === SaveAnswersFailure.type) {
              // Code to execute on API Failure Action dispatch
              if (callback) {
                callback(false);
              }
            }
          })
        )
      )
    )
  );

  constructor(
    private store: Store<fromRoot.State>,
    private participantListService: ParticipantsListService,
    private actions: Actions,
    private commonService: CommonService,
    private router: Router
  ) {
  }
}
